import { useEffect, useState } from "react";
import { Ban, MailWarning, UserCheck } from "lucide-react";
import { bulkGetPrimeContactStatus, setPrimeContactStatus, type PrimeContact } from "../api/CrmApi";
import Modal from "shared/Modal";
import Button, { type ButtonVariant } from "shared/Button";
import Alert from "shared/Alert";
import { FORM_CONTROL } from "shared/tableStyles";
import { apiErrorMessage } from "../apiError";
import CompanyPicker, { type CompanyOption } from "./CompanyPicker";

type PrimeContactStatus = "active" | "do_not_contact" | "bounced";

const STATUS_META: Record<PrimeContactStatus, { label: string; icon: typeof Ban }> = {
  active: { label: "Active", icon: UserCheck },
  do_not_contact: { label: "Do Not Contact", icon: Ban },
  bounced: { label: "Bounced", icon: MailWarning },
};

// Confirm button per target status: brand for clearing, danger for DNC, and
// a warning-toned primary for bounced (Button has no warning variant).
const CONFIRM_STYLE: Record<PrimeContactStatus, { variant: ButtonVariant; className?: string }> = {
  active: { variant: "primary" },
  do_not_contact: { variant: "danger" },
  bounced: { variant: "primary", className: "bg-warning text-warning-foreground hover:bg-warning/90" },
};

interface Props {
  email: string;
  displayName?: string;
  currentStatus: PrimeContactStatus;
  targetStatus: PrimeContactStatus;
  onClose: () => void;
  onSaved?: (prime: PrimeContact) => void;
}

export default function SetPrimeContactStatusModal({ email, displayName, currentStatus, targetStatus, onClose, onSaved }: Props) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const flagging = targetStatus !== "active";
  const meta = STATUS_META[targetStatus];
  const Icon = meta.icon;
  const confirmStyle = CONFIRM_STYLE[targetStatus];

  useEffect(() => {
    let cancelled = false;
    bulkGetPrimeContactStatus([email])
      .then((byEmail) => {
        if (cancelled) return;
        const status = byEmail[email.trim().toLowerCase()];
        // Only prefill when the reason on file actually describes what we're
        // about to set: clearing back to active (status_reason is always
        // currentStatus's own reason, so it's safe context there) or
        // re-affirming the SAME status. Switching to a *different* flagging
        // status (e.g. currently bounced, now marking do_not_contact) must
        // start blank — the existing reason describes the old status, not
        // the new one, and silently carrying it over would misrepresent why
        // this contact is actually being excluded now.
        if (status?.reason && (targetStatus === "active" || targetStatus === currentStatus)) {
          setReason(status.reason);
        }
      })
      .catch(() => {
        // No flag on file (or a lookup failure) just means starting fresh.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // currentStatus/targetStatus are stable for this modal's lifetime (a
    // fresh instance is mounted each time it opens) — only email needs to
    // re-trigger the lookup.
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  // The address is not on file at all: the server needs to know which company this person belongs to.
  const [needsCompany, setNeedsCompany] = useState(false);
  const [company, setCompany] = useState<CompanyOption | null>(null);
  const needsReason = flagging && reason.trim().length === 0;
  const missingCompany = needsCompany && !company;

  async function confirm() {
    if (needsReason || missingCompany) return;
    setSaving(true);
    setError(null);
    try {
      // Always send the reason (even unchanged/blank while clearing) — the
      // backend only honors an empty reason while flagging, so this can't
      // silently wipe history, but it does let an edit made while clearing
      // (e.g. correcting a typo in the historical text) actually persist
      // instead of being silently discarded.
      const prime = await setPrimeContactStatus(email, targetStatus, reason.trim(), company?.value);
      onSaved?.(prime);
      setSaved(true);
      setTimeout(onClose, 600);
    } catch (err: any) {
      if (err?.response?.status === 422 && err?.response?.data?.detail?.code === "company_required") {
        setNeedsCompany(true);
        setError(null);
      } else {
        setError(apiErrorMessage(err));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      size="md"
      title={flagging ? `Mark ${meta.label}` : "Set Status to Active"}
      description={displayName || email}
      icon={<Icon />}
      onClick={saving ? undefined : onClose}
      closeButtonCLick={saving ? undefined : onClose}
      footer={
        loading ? undefined : (
          <>
            <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant={confirmStyle.variant}
              size="sm"
              onClick={confirm}
              disabled={saving || needsReason || missingCompany}
              title={needsReason ? "Add a reason first" : missingCompany ? "Choose the company first" : undefined}
              className={confirmStyle.className}
            >
              {saving ? "Saving…" : saved ? "Saved" : flagging ? `Mark ${meta.label}` : "Set Active"}
            </Button>
          </>
        )
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-foreground">
            {targetStatus === "do_not_contact" &&
              "This will flag this person as Do Not Contact, excluding them from every future RFP outreach."}
            {targetStatus === "bounced" &&
              "This will flag this exact address as a confirmed dead mailbox, excluding it from every future RFP outreach."}
            {targetStatus === "active" &&
              `This will clear the current ${STATUS_META[currentStatus].label} flag, making this contact eligible for future outreach again.`}
          </p>
          {flagging && currentStatus !== "active" && currentStatus !== targetStatus && (
            <Alert tone="warning" className="text-[11px]">
              This person is currently marked {STATUS_META[currentStatus].label} — marking {meta.label} will
              replace that flag (a contact can only hold one status at a time). The {STATUS_META[currentStatus].label.toLowerCase()} reason will no longer be on record.
            </Alert>
          )}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              Reason{flagging ? "" : " (kept as history if previously set)"}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={
                targetStatus === "bounced"
                  ? "e.g. Hard bounce (550 5.4.1, Recipient address rejected) — confirmed via NDR, SmartLead didn't catch it."
                  : "e.g. Replied asking to be removed from all outreach — enrolled in 3 concurrent campaigns."
              }
              className={`w-full px-3 py-2 ${FORM_CONTROL} resize-none`}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            This flags this exact {targetStatus === "bounced" ? "ADDRESS" : "PERSON"} only — a colleague at the
            same company is unaffected. Future campaigns will hard-exclude this exact email address, even from an
            RFP their company would otherwise match on.
          </p>
          {needsCompany && (
            <div>
              <Alert tone="warning" className="text-[11px] mb-2">
                {email} is not on file yet, and every contact belongs to a company. Choose the company this person works for.
              </Alert>
              <CompanyPicker value={company} onChange={setCompany} autoFocus />
            </div>
          )}
          {needsReason && (
            <p className="text-[11px] text-warning-soft-foreground">
              A reason is required when flagging {meta.label.toLowerCase()} — it's the only record of why this
              contact was excluded from every future RFP.
            </p>
          )}
        </div>
      )}

      {error && (
        <Alert tone="danger" className="mt-4">{error}</Alert>
      )}
    </Modal>
  );
}
